import { Injectable, inject } from '@angular/core';
import LogRocket from 'logrocket';
import { environment } from 'src/environments/environment';
import packageInfo from '../../../package.json';
import { AppConfig } from '../store/types';
import { FeatureFlagsService } from './feature-flags.service';

const LOGROCKET_APP_ID = 'ufneyx/superpaywebapp';

type SessionRecordingMode = 'all' | 'selected';

/**
 * Arranque condicional de LogRocket.
 *
 * Antes se llamaba a `LogRocket.init()` en `main.ts` para TODOS los visitantes.
 * Ahora lo decide la configuración de SuperPay:
 *
 * - `enable_logrocket` (config de cliente, boolean): interruptor general.
 * - `logrocket_enabled_for` (config de cliente, 'all' | 'selected'):
 *   - `all`      → se graba a todo el mundo, desde el arranque de la app.
 *   - `selected` → solo a los usuarios cuyo correo está en la lista habilitada.
 *
 * La lista de correos NO viaja al navegador: vive en `logrocket_allowed_emails`,
 * una config de sistema que no es de cliente (visible en el panel del admin,
 * pero fuera de `/superpay-app-config`), y el backend solo responde un booleano
 * para el usuario del token (`GET v1/auth/feature-flags`).
 *
 * En modo `selected` la grabación empieza al identificarse el usuario (login o
 * restauración de sesión), no antes: hasta ese momento no se sabe quién es.
 */
@Injectable({ providedIn: 'root' })
export class SessionRecordingService {
  private readonly featureFlags = inject(FeatureFlagsService);

  private started = false;
  private policy: { enabled: boolean; mode: SessionRecordingMode } = { enabled: false, mode: 'all' };
  /**
   * Respuesta del backend cacheada (modo `selected`), junto con el usuario para
   * el que se pidió: el servicio es singleton y sobrevive a un logout/login sin
   * recarga, así que la decisión de un usuario no puede reutilizarse para otro.
   */
  private backendDecision?: { userKey: string; decision: Promise<boolean> };

  /**
   * `LogRocket.init()` no se puede deshacer: una vez arrancado graba hasta que
   * se descarga la página. En modo `selected` eso significa que, si un usuario
   * grabado cierra sesión y la SPA navega a `/login` sin recargar, el
   * formulario de credenciales del SIGUIENTE usuario (que puede no estar en la
   * lista) seguiría cayendo en la grabación. Cuando esto vale `true`, el
   * logout debe forzar una recarga completa para que ese login ocurra en una
   * página donde LogRocket nunca se inicializó.
   *
   * En modo `all` no aplica: ahí se graba a todo el mundo igualmente, así que
   * recargar solo añadiría coste sin cambiar qué se captura.
   */
  get requiresReloadOnLogout(): boolean {
    return this.started && this.policy.mode === 'selected';
  }

  /** Mismo criterio de entorno que tenía `main.ts`: solo producción real. */
  private get envAllowsRecording(): boolean {
    return environment.production && environment.env !== 'test' && environment.env !== 'sandbox';
  }

  /**
   * Se llama cada vez que se (re)carga la configuración de la app. En modo
   * `all` arranca la grabación de inmediato; en modo `selected` no hace nada
   * hasta que haya usuario.
   */
  applyConfig(config?: AppConfig) {
    if (!this.envAllowsRecording) return;

    const enabled = config?.enable_logrocket === true || (config as any)?.enable_logrocket === 'true';
    const rawMode = String((config as any)?.logrocket_enabled_for ?? 'all').trim().toLowerCase();
    const mode: SessionRecordingMode = rawMode === 'all' ? 'all' : 'selected';
    this.policy = { enabled, mode };

    if (enabled && mode === 'all') this.start();
  }

  /**
   * Identifica al usuario, arrancando antes la grabación si le corresponde.
   * Nunca rompe el login: cualquier fallo se ignora en silencio.
   */
  async identifyUser(user: { id: string | number; email?: string }, name?: string) {
    if (!this.envAllowsRecording || !this.policy.enabled) return;

    try {
      const userKey = String(user.id);
      if (this.policy.mode === 'selected' && !(await this.isEnabledForUser(userKey))) return;

      this.start();
      LogRocket.identify(String(user.id), {
        email: user.email,
        name
      });
    } catch {}
  }

  /** El backend resuelve la pertenencia a la lista: aquí solo llega un booleano. */
  private isEnabledForUser(userKey: string): Promise<boolean> {
    if (this.backendDecision?.userKey !== userKey) {
      this.backendDecision = {
        userKey,
        decision: this.featureFlags.load(userKey).then((flags) => flags.sessionRecording)
      };
    }
    return this.backendDecision.decision;
  }

  /** `LogRocket.init` es idempotente por diseño aquí: solo se ejecuta una vez. */
  private start() {
    if (this.started) return;
    this.started = true;
    LogRocket.init(LOGROCKET_APP_ID, { release: packageInfo.version });
  }
}
